import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Check, RotateCcw, Pencil, ScrollText, Loader2, CheckCheck, FolderKanban } from 'lucide-react';
import { ProjectJobSelect, errorDetailText } from '../ProjectJobSelect';
import { useToast } from '../../hooks/use-toast';
import { EditEntryDialog } from '../time-editor/EditEntryDialog';
import { AuditPanel } from './AuditPanel';
import { NotesModal, SeverityBadge, StatusPill, EXCEPTION_TYPE_LABELS, utcDate, fmtTime, fmtDate } from './shared';

const API = `${window.__BACKEND_URL__}/api`;

const AllocDot = ({ allocated, payable }) => {
  const none = !allocated || allocated <= 0;
  const balanced = !none && Math.abs(allocated - payable) <= 0.05;
  const color = none ? '#EF4444' : balanced ? '#00C896' : '#FFA000';
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      <span style={{ color: '#A0AEC0' }}>{(allocated || 0).toFixed(1)}/{payable.toFixed(1)} hrs</span>
    </span>
  );
};

export const ApprovalQueue = ({ token, onChanged }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [entries, setEntries] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [filters, setFilters] = useState({ start: '', end: '', worker: 'all', excOnly: false, unattributed: false });
  const [unattr, setUnattr] = useState({ count: 0, hours: 0 });
  const [expanded, setExpanded] = useState(null);
  const [excMap, setExcMap] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [returning, setReturning] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [auditEntry, setAuditEntry] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in_date: '', clock_in_time: '', clock_out_date: '', clock_out_time: '', reason: '' });
  const [editBusy, setEditBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // Bulk project re-allocation (Sep 2026) — attribution only.
  const [selected, setSelected] = useState([]);
  const [realloc, setRealloc] = useState(null); // { projectId, jobId }
  const [reallocBusy, setReallocBusy] = useState(false);
  const [reallocResult, setReallocResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.start) params.start_date = filters.start;
      if (filters.end) params.end_date = filters.end;
      if (filters.worker !== 'all') params.user_id = filters.worker;
      if (filters.excOnly) params.has_exceptions = true;
      if (filters.unattributed) params.unattributed = true;
      const { data } = await axios.get(`${API}/time/supervisor-queue`, { ...auth, params });
      setEntries(data.entries || []);
      setUnattr({ count: data.unattributed_count || 0, hours: data.unattributed_hours || 0 });
    } catch (e) { setEntries([]); }
  }, [token, filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/users?scope=all`, auth)
      .then(({ data }) => setWorkers((data || []).filter((u) => !u.deleted_at)))
      .catch(() => setWorkers([]));
  }, [token]); // eslint-disable-line

  const refreshAll = () => { load(); onChanged?.(); };

  const loadExceptions = async (entryId) => {
    try {
      const { data } = await axios.get(`${API}/time/${entryId}/exceptions`, auth);
      setExcMap((m) => ({ ...m, [entryId]: data.exceptions || [] }));
    } catch (e) { setExcMap((m) => ({ ...m, [entryId]: [] })); }
  };

  const toggleExpand = (entryId) => {
    if (expanded === entryId) { setExpanded(null); return; }
    setExpanded(entryId);
    loadExceptions(entryId);
  };

  const approve = async (entry) => {
    setBusyId(entry.id);
    try {
      await axios.put(`${API}/time/${entry.id}/supervisor-approve`, {}, auth);
      toast({ title: 'Timesheet approved', description: `${entry.worker_name} — ${fmtDate(entry.clock_in)}` });
      refreshAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.blocking_exceptions) {
        setBlocked({ entry, exceptions: detail.blocking_exceptions });
      } else {
        toast({ title: 'Could not approve', description: typeof detail === 'string' ? detail : 'Try again', variant: 'destructive' });
      }
    } finally { setBusyId(null); }
  };

  const doReturn = async (reason) => {
    setBusyId(returning.id);
    try {
      await axios.put(`${API}/time/${returning.id}/supervisor-return`, { reason }, auth);
      toast({ title: 'Timesheet returned', description: 'The worker has been notified.' });
      setReturning(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not return', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const doResolve = async (notes) => {
    const { exceptionId, entryId } = resolving;
    try {
      await axios.put(`${API}/time/exceptions/${exceptionId}/resolve`, { resolution_notes: notes }, auth);
      toast({ title: 'Exception resolved' });
      setResolving(null);
      setBlocked(null);
      await loadExceptions(entryId);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not resolve', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const openEdit = (entry) => {
    const ci = utcDate(entry.clock_in);
    const co = entry.clock_out ? utcDate(entry.clock_out) : null;
    const pad = (n) => String(n).padStart(2, '0');
    const dateOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const timeOf = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setEditForm({
      clock_in_date: dateOf(ci), clock_in_time: timeOf(ci),
      clock_out_date: co ? dateOf(co) : '', clock_out_time: co ? timeOf(co) : '',
      reason: '',
    });
    setEditing(entry);
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const newIn = new Date(`${editForm.clock_in_date}T${editForm.clock_in_time}`);
      const newOut = editForm.clock_out_date && editForm.clock_out_time
        ? new Date(`${editForm.clock_out_date}T${editForm.clock_out_time}`) : null;
      await axios.put(`${API}/time-entries/${editing.id}/edit`, {
        entry_id: editing.id,
        new_clock_in: newIn.toISOString(),
        new_clock_out: newOut ? newOut.toISOString() : null,
        reason: editForm.reason,
      }, auth);
      toast({ title: 'Time entry updated' });
      setEditing(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not save edit', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setEditBusy(false); }
  };

  const toggleSelected = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const allSelected = entries?.length > 0 && selected.length === entries.length;
  const toggleAll = () => setSelected(allSelected ? [] : entries.map((e) => e.id));

  const doRealloc = async () => {
    setReallocBusy(true);
    try {
      const { data } = await axios.post(`${API}/time-entries/bulk-reassign-project`,
        { entry_ids: selected, project_id: realloc.projectId, job_id: realloc.jobId || null }, auth);
      setReallocResult(data);
      setRealloc(null);
      setSelected([]);
      refreshAll();
    } catch (e) {
      toast({ title: 'Re-allocation failed', description: errorDetailText(e), variant: 'destructive' });
    } finally { setReallocBusy(false); }
  };

  const bulkApprove = async () => {
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/time/supervisor-bulk-approve`,
        { entry_ids: entries.map((e) => e.id) }, auth);
      setBulkConfirm(false);
      setBulkResult(data);
      refreshAll();
    } catch (e) {
      toast({ title: 'Bulk approve failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBulkBusy(false); }
  };

  if (entries === null) {
    return <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>;
  }

  const anyHighUnresolved = entries.some((e) => e.has_unresolved_exceptions);
  const payableOf = (e) => {
    const dur = e.clock_out ? Math.max(0, (utcDate(e.clock_out) - utcDate(e.clock_in)) / 3600000) : 0;
    return Math.max(0, dur - (e.total_break_minutes || 0) / 60);
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="ht-card p-3 flex flex-wrap items-center gap-2" data-testid="approvals-filter-bar">
        <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}
          data-testid="approvals-filter-start" />
        <span style={{ color: '#64748B' }}>→</span>
        <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}
          data-testid="approvals-filter-end" />
        <select value={filters.worker} onChange={(e) => setFilters({ ...filters, worker: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}
          data-testid="approvals-filter-worker">
          <option value="all">All workers</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none ml-1" style={{ color: '#A0AEC0' }}>
          <input type="checkbox" checked={filters.excOnly}
            onChange={(e) => setFilters({ ...filters, excOnly: e.target.checked })}
            className="accent-amber-500"
            data-testid="approvals-filter-exceptions-only" />
          Show exceptions only
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: '#A0AEC0' }} title="Rows with no project or on the General Work catch-all — attribution only, payroll is unaffected">
          <input type="checkbox" checked={filters.unattributed}
            onChange={(e) => setFilters({ ...filters, unattributed: e.target.checked })}
            className="accent-emerald-500"
            data-testid="approvals-filter-unattributed" />
          Legacy / no project only
          {unattr.count > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(16,185,129,0.15)', color: '#34D399' }} data-testid="approvals-unattributed-count">{unattr.count} · {unattr.hours} h</span>}
        </label>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setRealloc({ projectId: null, jobId: null })}
            disabled={selected.length === 0}
            className="border-[#2D3748] text-emerald-300 hover:bg-emerald-500/10"
            title={selected.length === 0 ? 'Tick entries first' : ''}
            data-testid="reallocate-project-btn">
            <FolderKanban className="w-4 h-4 mr-1.5" /> Re-allocate project ({selected.length})
          </Button>
          {anyHighUnresolved && entries.length > 0 && (
            <span className="text-[11px]" style={{ color: '#FB923C' }} data-testid="approve-all-blocked-hint">
              Resolve high-severity exceptions first
            </span>
          )}
          <Button size="sm" onClick={() => setBulkConfirm(true)}
            disabled={entries.length === 0 || anyHighUnresolved}
            title={anyHighUnresolved ? 'Resolve high-severity exceptions before bulk approving' : ''}
            className="bg-[#00C896] hover:bg-emerald-600 text-white font-semibold"
            data-testid="approve-all-btn">
            <CheckCheck className="w-4 h-4 mr-1.5" /> Approve All ({entries.length})
          </Button>
        </div>
      </div>

      {/* Entry table */}
      <div className="ht-card p-0 overflow-x-auto" data-testid="approvals-queue-table-wrap">
        {entries.length === 0 ? (
          <p className="text-sm text-center py-12" style={{ color: '#64748B' }} data-testid="approvals-queue-empty">
            No timesheets waiting for your approval. 🎉
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="approvals-queue-table">
            <thead>
              <tr style={{ borderBottom: '1px solid #2D3748' }}>
                <th className="py-2.5 px-3">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-emerald-500" data-testid="queue-select-all" />
                </th>
                {['Worker', 'Project', 'Date', 'In', 'Out', 'Reg', 'OT', 'Allocated', 'Exceptions', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 font-medium text-xs whitespace-nowrap" style={{ color: '#64748B' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const payable = payableOf(e);
                const excs = excMap[e.id];
                return (
                  <React.Fragment key={e.id}>
                    <tr style={{ borderBottom: expanded === e.id ? 'none' : '1px solid #1F2D3F' }} data-testid={`queue-row-${e.id}`}>
                      <td className="py-2.5 px-3">
                        <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleSelected(e.id)} className="accent-emerald-500" data-testid={`queue-select-${e.id}`} />
                      </td>
                      <td className="py-2.5 px-3 text-white font-medium whitespace-nowrap">{e.worker_name || '—'}</td>
                      <td className="py-2.5 px-3 text-xs whitespace-nowrap" data-testid={`queue-project-${e.id}`}>
                        {e.project_id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono" style={{ color: '#10B981' }}>{e.project_number}</span>
                            <span style={{ color: '#CBD5E1' }}>{e.project_name}</span>
                            {e.attribution_migrated_source && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(148,163,184,0.15)', color: '#CBD5E1' }} title="Assigned by the historical migration — re-allocate if it belongs to a real project">migrated</span>
                            )}
                            {e.job_title && <span className="text-[11px]" style={{ color: '#64748B' }}>· {e.job_title}</span>}
                          </span>
                        ) : <span className="text-[11px] font-semibold" style={{ color: '#F87171' }}>No project</span>}
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap" style={{ color: '#A0AEC0' }}>{fmtDate(e.clock_in)}</td>
                      <td className="py-2.5 px-3 whitespace-nowrap" style={{ color: '#A0AEC0' }}>{fmtTime(e.clock_in)}</td>
                      <td className="py-2.5 px-3 whitespace-nowrap" style={{ color: '#A0AEC0' }}>{e.clock_out ? fmtTime(e.clock_out) : '—'}</td>
                      <td className="py-2.5 px-3 text-white whitespace-nowrap">{(e.regular_hours ?? 0).toFixed(2)}</td>
                      <td className="py-2.5 px-3 font-semibold whitespace-nowrap" style={{ color: (e.overtime_hours || 0) > 0 ? '#FFA000' : '#A0AEC0' }}
                        data-testid={`ot-hours-${e.id}`}>
                        {(e.overtime_hours ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-xs whitespace-nowrap" data-testid={`allocated-${e.id}`}>
                        <AllocDot allocated={e.total_allocated_hours} payable={payable} />
                      </td>
                      <td className="py-2.5 px-3">
                        {(e.exception_count || 0) > 0 || e.has_unresolved_exceptions ? (
                          <button onClick={() => toggleExpand(e.id)}
                            className="px-2 py-0.5 rounded-full text-[11px] font-bold"
                            style={{
                              background: e.has_unresolved_exceptions ? 'rgba(239,68,68,0.15)' : 'rgba(255,160,0,0.15)',
                              color: e.has_unresolved_exceptions ? '#F87171' : '#FBBF24',
                            }}
                            data-testid={`exception-badge-${e.id}`}>
                            ⚠ {e.exception_count || '!'}
                          </button>
                        ) : <span className="text-xs" style={{ color: '#4A5568' }}>—</span>}
                      </td>
                      <td className="py-2.5 px-3"><StatusPill status={e.approval_status} /></td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <button onClick={() => approve(e)} disabled={busyId === e.id || e.has_unresolved_exceptions}
                            title={e.has_unresolved_exceptions ? 'Resolve high-severity exceptions first' : 'Approve'}
                            className="p-1.5 rounded-lg disabled:opacity-35"
                            style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                            data-testid={`approve-btn-${e.id}`}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setReturning(e)} title="Return to worker"
                            className="p-1.5 rounded-lg"
                            style={{ background: 'rgba(255,120,80,0.15)', color: '#FB923C' }}
                            data-testid={`return-btn-${e.id}`}>
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEdit(e)} title="Edit times"
                            className="p-1.5 rounded-lg"
                            style={{ background: 'rgba(30,136,229,0.15)', color: '#60A5FA' }}
                            data-testid={`edit-btn-${e.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => setAuditEntry(e)} title="Audit trail"
                            className="p-1.5 rounded-lg"
                            style={{ background: 'rgba(160,174,192,0.12)', color: '#A0AEC0' }}
                            data-testid={`audit-btn-${e.id}`}>
                            <ScrollText className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded === e.id && (
                      <tr style={{ borderBottom: '1px solid #1F2D3F' }} data-testid={`exceptions-expanded-${e.id}`}>
                        <td colSpan={12} className="px-4 pb-3">
                          {!excs ? (
                            <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#1E88E5' }} />
                          ) : excs.length === 0 ? (
                            <p className="text-xs py-2" style={{ color: '#64748B' }}>No exceptions on this entry.</p>
                          ) : (
                            <div className="space-y-1.5 pt-1">
                              {excs.map((x) => (
                                <div key={x.id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                                  style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                                  data-testid={`inline-exception-${x.id}`}>
                                  <div className="flex items-center gap-2 min-w-0">
                                    <SeverityBadge severity={x.severity} />
                                    <span className="text-xs font-semibold text-white whitespace-nowrap">{EXCEPTION_TYPE_LABELS[x.exception_type] || x.exception_type}</span>
                                    <span className="text-xs truncate" style={{ color: '#A0AEC0' }}>{x.description}</span>
                                    {['resolved', 'overridden'].includes(x.status) && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}>{x.status}</span>
                                    )}
                                  </div>
                                  {!['resolved', 'overridden'].includes(x.status) && (
                                    <button onClick={() => setResolving({ exceptionId: x.id, entryId: e.id })}
                                      className="text-xs font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap"
                                      style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                                      data-testid={`resolve-btn-${x.id}`}>
                                      ✅ Resolve
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Return reason modal */}
      {returning && (
        <NotesModal title={`Return ${returning.worker_name}'s timesheet`}
          description="Tell the worker what needs correcting — they'll be notified and can resubmit."
          placeholder="e.g. Clock-out looks wrong — you left site at 3:30, not 5:30"
          submitLabel="Return to Worker" destructive busy={busyId === returning.id}
          onSubmit={doReturn} onClose={() => setReturning(null)} testId="return-modal" />
      )}

      {/* Resolve notes modal */}
      {resolving && (
        <NotesModal title="Resolve exception"
          description="Explain how this was verified/handled (kept in the permanent audit trail)."
          placeholder="e.g. Confirmed with worker — legitimate overtime approved verbally"
          submitLabel="Resolve" onSubmit={doResolve} onClose={() => setResolving(null)} testId="resolve-modal" />
      )}

      {/* Blocked approve modal */}
      {blocked && (
        <Dialog open onOpenChange={(o) => !o && setBlocked(null)}>
          <DialogContent className="max-w-md" data-testid="blocked-approve-modal">
            <DialogHeader>
              <DialogTitle>Approval blocked</DialogTitle>
              <DialogDescription>
                Resolve or override these high-severity exceptions on {blocked.entry.worker_name}'s timesheet first.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              {blocked.exceptions.map((x) => (
                <div key={x.id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                  style={{ background: '#0B1321', border: '1px solid rgba(239,68,68,0.35)' }}>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white">{EXCEPTION_TYPE_LABELS[x.exception_type] || x.exception_type}</p>
                    <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>{x.description}</p>
                  </div>
                  <button onClick={() => setResolving({ exceptionId: x.id, entryId: blocked.entry.id })}
                    className="text-xs font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap"
                    style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                    data-testid={`blocked-resolve-${x.id}`}>
                    Resolve
                  </button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlocked(null)} data-testid="blocked-modal-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk approve confirm + result */}
      {bulkConfirm && (
        <Dialog open onOpenChange={(o) => !o && setBulkConfirm(false)}>
          <DialogContent className="max-w-sm" data-testid="bulk-approve-confirm">
            <DialogHeader>
              <DialogTitle>Approve all {entries.length} timesheets?</DialogTitle>
              <DialogDescription>Entries with unresolved high-severity exceptions will be skipped automatically.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkConfirm(false)} data-testid="bulk-confirm-cancel">Cancel</Button>
              <Button onClick={bulkApprove} disabled={bulkBusy} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="bulk-confirm-approve">
                {bulkBusy ? 'Approving…' : 'Approve All'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {bulkResult && (
        <Dialog open onOpenChange={(o) => !o && setBulkResult(null)}>
          <DialogContent className="max-w-sm" data-testid="bulk-approve-result">
            <DialogHeader>
              <DialogTitle>Bulk approval complete</DialogTitle>
              <DialogDescription data-testid="bulk-result-summary">
                {bulkResult.approved} approved, {bulkResult.failed} skipped (unresolved exceptions)
              </DialogDescription>
            </DialogHeader>
            {bulkResult.failed_entries?.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {bulkResult.failed_entries.map((f) => (
                  <p key={f.id} className="text-xs" style={{ color: '#FB923C' }}>{f.user_name || f.id}: {f.reason}</p>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setBulkResult(null)} className="bg-[#1E88E5] text-white" data-testid="bulk-result-close">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk project re-allocation */}
      {realloc && (
        <Dialog open onOpenChange={(o) => !o && setRealloc(null)}>
          <DialogContent className="max-w-md" data-testid="reallocate-dialog">
            <DialogHeader>
              <DialogTitle>Re-allocate {selected.length} entr{selected.length === 1 ? 'y' : 'ies'}</DialogTitle>
              <DialogDescription>Attribution only — clock times, pay and approval status are untouched. Locked/paid entries are skipped.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <ProjectJobSelect token={token} projectId={realloc.projectId} jobId={realloc.jobId} onChange={setRealloc} testIdPrefix="realloc" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRealloc(null)} data-testid="realloc-cancel">Cancel</Button>
              <Button onClick={doRealloc} disabled={reallocBusy || !realloc.projectId} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="realloc-confirm">
                {reallocBusy ? 'Saving…' : 'Re-allocate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {reallocResult && (
        <Dialog open onOpenChange={(o) => !o && setReallocResult(null)}>
          <DialogContent className="max-w-sm" data-testid="reallocate-result">
            <DialogHeader>
              <DialogTitle>Re-allocation complete</DialogTitle>
              <DialogDescription data-testid="realloc-result-summary">
                {reallocResult.updated} moved to {reallocResult.project?.project_number} — {reallocResult.project?.name}
                {reallocResult.job ? ` · ${reallocResult.job.title}` : ''}; {reallocResult.skipped?.length || 0} skipped.
              </DialogDescription>
            </DialogHeader>
            {reallocResult.skipped?.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {reallocResult.skipped.map((f) => (
                  <p key={f.id} className="text-xs" style={{ color: '#FB923C' }}>{f.user_name || f.id}: {f.reason}</p>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setReallocResult(null)} className="bg-[#1E88E5] text-white" data-testid="realloc-result-close">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit dialog (reuses admin time editor) */}
      <EditEntryDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}
        entry={editing ? { ...editing, user_name: editing.worker_name } : null}
        form={editForm} setForm={setEditForm} onSave={saveEdit} isLoading={editBusy} />

      {/* Audit slide-out */}
      {auditEntry && <AuditPanel token={token} entry={auditEntry} onClose={() => setAuditEntry(null)} />}
    </div>
  );
};

export default ApprovalQueue;
